import { useEffect } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import Switch from 'components/ui/Switch';
import Select from 'components/ui/Select';
import COLOR from 'constants/color';
import { Shield } from 'assets/icons/revamp';
import Card from 'components/ui/Card';
import { t } from 'services/i18n.service';
import { EMPTY_STRING, MODAL_TYPES } from 'constants/general';
import {
  updateMoreOptionFocusMode,
  updateMoreOptionIsUseFocusModeChecked,
  updateMoreOptionAllowedApps,
  updateMoreOptionURLs
} from 'store/reducer/modal/slice';
import { moreOptionsAllowedAppsFocusModeSelector } from 'store/reducer/setting/selectors';

const FocusMode: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    focus_modes,
    activity: focusModeActivity,
    isUseFocusModeChecked,
    isFetchingUserFocusModes
  } = useAppSelectorShallow(moreOptionsAllowedAppsFocusModeSelector);

  useEffect(() => {
    dispatch(
      updateMoreOptionIsUseFocusModeChecked(
        Boolean(focusModeActivity?.allowed_focus_mode_id)
      )
    );
  }, [focusModeActivity, dispatch]);

  const handleToggleFocusMode = (checked: boolean) => {
    dispatch(updateMoreOptionIsUseFocusModeChecked(checked));
    if (checked && focus_modes?.length) {
      dispatch(updateMoreOptionFocusMode(focus_modes[0]?.id));
      dispatch(updateMoreOptionAllowedApps([]));
      dispatch(
        updateMoreOptionURLs({
          modal_type: MODAL_TYPES.ALLOWED_URLS,
          urls: []
        })
      );
    } else {
      dispatch(updateMoreOptionFocusMode(EMPTY_STRING));
    }
  };

  const handleFocusModeChange = (focusModeId: string) => {
    dispatch(updateMoreOptionFocusMode(focusModeId));
  };

  return (
    <Card className='flex flex-col ~gap-3/4'>
      <div className='flex flex-col ~gap-1/2'>
        <div className='flex items-center ~gap-3/6'>
          <h3 className='~text-xs/base font-semibold flex items-center ~gap-1/2'>
            <Shield styles='~w-4/5 ~h-4/5' fill={COLOR.PRIMARY.LIGHT_900} />
            {t('tabs_revamp.allowed_apps_tab.use_focus_mode')}
          </h3>
          <Switch
            checked={isUseFocusModeChecked}
            onChange={handleToggleFocusMode}
            size='medium'
            variant='default'
          />
        </div>
        <p className='~text-xs/sm text-gray-600 dark:text-gray-400 ~ml-0/0'>
          {t('tabs_revamp.allowed_apps_tab.use_focus_mode_description')}
        </p>
      </div>
      {isUseFocusModeChecked && (
        <div className='~ml-0/0'>
          {focus_modes?.length ? (
            <Select
              value={focusModeActivity?.allowed_focus_mode_id || ''}
              options={focus_modes.map((mode) => ({
                value: mode.id,
                label: mode.name
              }))}
              onChange={(value) => handleFocusModeChange(value as string)}
              size='sm'
              variant='filled'
              className='w-full ~min-h-4/8'
              isFullWidth={true}
              isDisabled={isFetchingUserFocusModes}
            />
          ) : (
            <p className='w-full flex justify-center ~text-xs/sm font-semibold'>
              {t('no_focus_mode_found')}
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default FocusMode;
